import { useCallback, useEffect, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useAuthStore } from '@store/Auth'
import { useChatStore, DEFAULT_CHAT_PAGE_SIZE } from '@store/Chat'
import { IUserInfo, IUserSettings } from '@store/User/types'
import {
  ChatMessage,
  ChatState,
  ChatTranslationSettings,
  PendingChatMessage
} from '@store/Chat/types'
import { useUserStore } from '@store/User'
import { normalizeUtcOffsetHours } from '@utils/date'

const toPendingChatMessage = (pending: PendingChatMessage): ChatMessage => ({
  id: pending.id,
  content: pending.content,
  isSender: true,
  contentType: pending.contentType,
  documents: pending.documents,
  documentId:
    pending.contentType === 'FILE' ? Number(pending.value) : undefined,
  isPending: true
})

const selectRoomViewState = (userId: number) => (state: ChatState) => ({
  isFlipperWSConnected: state.isFlipperWSConnected,
  storedMessages: state.messagesByUserId[userId],
  pendingMessagesById: state.pendingMessagesById,
  isLoading: state.isLoadingMessagesByUserId[userId] ?? false,
  isOtherTyping: state.isOtherTypingByUserId[userId] ?? false,
  hasNextPage: state.hasNextPageByUserId[userId] ?? false,
  currentPage: state.pageByUserId[userId] ?? 0,
  storedTranslation: state.translationByUserId[userId]
})

export function useChatRoom(
  userId: number,
  roomId?: number,
  userInfo?: IUserInfo | null
) {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )

  const professionalId = useAuthStore(state => state.userId)
  const ownerId = professionalId

  const roomSelector = useMemo(() => selectRoomViewState(userId), [userId])

  const accessToken = useAuthStore(state => state.accessToken)

  const {
    isFlipperWSConnected,
    storedMessages,
    pendingMessagesById,
    isLoading,
    isOtherTyping,
    hasNextPage,
    currentPage,
    storedTranslation
  } = useChatStore(useShallow(roomSelector))

  const { getUserSettings } = useUserStore.getState()
  const [userSettings, setUserSettings] = useState<IUserSettings | null>(null)

  useEffect(() => {
    getUserSettings(userId).then(setUserSettings)
  }, [getUserSettings, userId])

  const memberDisplayName = (
    userInfo?.preferred_name ||
    [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ') ||
    ''
  ).trim()

  const memberTimezoneOffset = normalizeUtcOffsetHours(userSettings?.timezone)

  const {
    connectSocket,
    setActiveUserId,
    fetchMessages,
    sendTextMessage,
    sendTyping,
    uploadDocument,
    sendFileMessage,
    setTranslation,
    saveLanguageSetting,
    getDocumentUrl,
    markAsRead
  } = useChatStore.getState()

  const messages = storedMessages ?? []
  const translation: ChatTranslationSettings = storedTranslation ?? {
    on: false,
    language: 'ENGLISH'
  }

  const pendingMessages = useMemo(
    () =>
      Object.values(pendingMessagesById)
        .filter(
          pending =>
            pending.userId === userId &&
            !messages.some(message => message.id === pending.id)
        )
        .map(toPendingChatMessage),
    [pendingMessagesById, userId, messages]
  )

  const allMessages = useMemo(
    () => [...messages, ...pendingMessages],
    [messages, pendingMessages]
  )

  const shouldBeSendingMessages = isOnline && isFlipperWSConnected
  const isReady = !!userId && !Number.isNaN(userId) && !!accessToken

  useEffect(() => {
    if (!isReady) {
      return
    }

    setActiveUserId(userId, ownerId ?? null, roomId ?? null)
    connectSocket()
    fetchMessages(userId, 0, DEFAULT_CHAT_PAGE_SIZE, undefined, ownerId)
    markAsRead(userId)

    const handleStatusChange = () => {
      setIsOnline(navigator.onLine)
      if (navigator.onLine) {
        connectSocket()
      }
    }

    window.addEventListener('online', handleStatusChange)
    window.addEventListener('offline', handleStatusChange)

    return () => {
      setActiveUserId(null, null, null)
      window.removeEventListener('online', handleStatusChange)
      window.removeEventListener('offline', handleStatusChange)
    }
  }, [userId, accessToken, isReady, ownerId, roomId])

  useEffect(() => {
    if (!isReady || messages.length === 0) {
      return
    }

    markAsRead(userId)
  }, [messages.length, userId, isReady])

  const loadMoreMessages = useCallback(async () => {
    if (!hasNextPage || isLoading) {
      return
    }

    await fetchMessages(
      userId,
      currentPage + 1,
      DEFAULT_CHAT_PAGE_SIZE,
      undefined,
      ownerId
    )
  }, [currentPage, fetchMessages, hasNextPage, isLoading, ownerId, userId])

  const handleSendText = useCallback(
    (text: string) =>
      sendTextMessage(userId, text, {
        roomId: roomId ?? 0,
        ownerId: ownerId ?? 0
      }),
    [ownerId, roomId, sendTextMessage, userId]
  )

  const handleSendTyping = useCallback(
    (value: 'start' | 'stop') => {
      if (!shouldBeSendingMessages || !roomId) {
        return
      }
      sendTyping(userId, roomId, value)
    },
    [sendTyping, shouldBeSendingMessages, userId, roomId]
  )

  const handleSendFile = useCallback(
    async (file: File) => {
      const result = await uploadDocument(userId, file)
      if (!result) {
        return false
      }

      sendFileMessage(
        userId,
        result.documentId,
        {
          roomId: roomId ?? 0,
          ownerId: ownerId ?? 0
        },
        result.documents
      )

      return true
    },
    [ownerId, roomId, sendFileMessage, uploadDocument, userId]
  )

  const handleTranslationToggle = useCallback(
    async (on: boolean, language: string) => {
      const success = await setTranslation(userId, on, language)
      if (!success) {
        return false
      }

      await saveLanguageSetting(userId, language)
      await fetchMessages(userId, 0, DEFAULT_CHAT_PAGE_SIZE, language, ownerId)
      return true
    },
    [fetchMessages, ownerId, saveLanguageSetting, setTranslation, userId]
  )

  const handleGetDocumentUrl = useCallback(
    (documentId: number) => getDocumentUrl(userId, documentId),
    [getDocumentUrl, userId]
  )

  return {
    isOnline,
    isLoading,
    isOtherTyping,
    isReady,
    shouldBeSendingMessages,
    allMessages,
    hasNextPage,
    translation,
    loadMoreMessages,
    handleSendText,
    handleSendTyping,
    handleSendFile,
    handleTranslationToggle,
    handleGetDocumentUrl,
    memberTimezoneOffset,
    memberDisplayName
  }
}
